import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  pharmaciesData, patientPharmacyAssociations
} from "@/data/platformData";
import {
  Building2, ArrowLeft, MapPin, Phone, Mail, Star, Plus, Trash2, Heart
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";

const CURRENT_PATIENT_ID = "1";

export const PatientPharmaciesPage = () => {
  const navigate = useNavigate();
  const [associations, setAssociations] = useState(
    patientPharmacyAssociations.filter(a => a.patientId === CURRENT_PATIENT_ID)
  );
  const [showAddDialog, setShowAddDialog] = useState(false);

  const associatedIds = new Set(associations.map(a => a.pharmacyId));
  const availablePharmacies = pharmaciesData.filter(p => !associatedIds.has(p.id));

  const setPreferred = (pharmacyId) => {
    setAssociations(prev => prev.map(a => ({ ...a, isPreferred: a.pharmacyId === pharmacyId })));
    toast.success("Farmacia preferita aggiornata", { description: "Sarà selezionata di default nelle prossime prenotazioni." });
  };

  const removeAssociation = (pharmacyId) => {
    const wasPreferred = associations.find(a => a.pharmacyId === pharmacyId)?.isPreferred;
    setAssociations(prev => {
      const next = prev.filter(a => a.pharmacyId !== pharmacyId);
      if (wasPreferred && next.length > 0) {
        next[0].isPreferred = true;
      }
      return next;
    });
    toast.success("Farmacia rimossa");
  };

  const addAssociation = (pharmacyId) => {
    setAssociations(prev => [...prev, {
      patientId: CURRENT_PATIENT_ID,
      pharmacyId,
      isPreferred: prev.length === 0,
      associatedAt: new Date().toISOString().slice(0, 10)
    }]);
    setShowAddDialog(false);
    toast.success("Farmacia associata");
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/patient")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Home
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-4 w-4 text-emerald-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Le mie farmacie</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Farmacie associate</h1>
            <p className="text-sm text-muted-foreground mt-1">Gestisci le farmacie che possono accedere al tuo storico</p>
          </div>
          {availablePharmacies.length > 0 && (
            <Button variant="premium" onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="pharmacies-add-btn">
              <Plus className="h-4 w-4" /> Associa farmacia
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3">
        {associations.length === 0 && (
          <Card className="border-dashed border-2">
            <CardContent className="p-8 text-center">
              <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium mb-1">Nessuna farmacia associata</p>
              <p className="text-xs text-muted-foreground mb-4">Associa una farmacia per accedere ai servizi</p>
              <Button variant="outline" onClick={() => setShowAddDialog(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Associa la prima
              </Button>
            </CardContent>
          </Card>
        )}
        {associations.map(a => {
          const ph = pharmaciesData.find(p => p.id === a.pharmacyId);
          if (!ph) return null;
          return (
            <Card key={a.pharmacyId} className={a.isPreferred ? "border-emerald-300 bg-emerald-50/30" : ""} data-testid={`pharmacy-card-${a.pharmacyId}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{ph.name}</h3>
                      {a.isPreferred && (
                        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 gap-1">
                          <Star className="h-3 w-3 fill-current" /> Preferita
                        </Badge>
                      )}
                    </div>
                    <div className="grid gap-1 mt-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ph.address}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {ph.phone}</span>
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {ph.email}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">Associata il {a.associatedAt}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {!a.isPreferred && (
                      <Button variant="outline" size="sm" onClick={() => setPreferred(a.pharmacyId)} className="gap-1" data-testid={`pharmacy-prefer-${a.pharmacyId}`}>
                        <Heart className="h-3 w-3" /> Imposta preferita
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => removeAssociation(a.pharmacyId)} data-testid={`pharmacy-remove-${a.pharmacyId}`}>
                      <Trash2 className="h-3 w-3" /> Rimuovi
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Associa una farmacia</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {availablePharmacies.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Tutte le farmacie disponibili sono già associate.</p>
            )}
            {availablePharmacies.map(p => (
              <button
                key={p.id}
                onClick={() => addAssociation(p.id)}
                className="w-full text-left p-3 rounded-lg border hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                data-testid={`pharmacy-add-${p.id}`}
              >
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.address}</p>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientPharmaciesPage;
